import type { ForceField } from "../physics/ForceField";
import type { Divider } from "../entities/Divider";
import type { WindParticles } from "../render/WindParticles";
import { clamp } from "../core/math";
import { BUY_ME_A_COFFEE_URL } from "../support";
import { AboutOverlay } from "./AboutOverlay";
import { coffeeSvg } from "./chromeIcons";

interface Letter {
  el: HTMLSpanElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const SPRING = 28;
const DAMP = 5.2;
const WIND_PX = 620000;
const MAX_ACCEL = 160;
const MAX_OFF = 22;

export class StartOverlay {
  private readonly root: HTMLDivElement;
  private readonly white: HTMLDivElement;
  private readonly dust: HTMLCanvasElement;
  private readonly dustCtx: CanvasRenderingContext2D;
  private readonly word: HTMLDivElement;
  private readonly button: HTMLButtonElement;
  private readonly letters: Letter[] = [];
  private open = true;
  private leaveTimer = 0;
  private levelsBtn: HTMLButtonElement | null = null;
  private readonly chrome: HTMLDivElement;
  private readonly about: AboutOverlay;

  constructor(
    private readonly host: HTMLElement,
    onStart: () => void,
    word: string,
    startLabel: string,
    onOpenPanel?: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.className = "onirya-wake onirya-start is-active";
    this.root.setAttribute("aria-hidden", "false");

    this.white = document.createElement("div");
    this.white.className = "onirya-wake-white";
    this.white.style.opacity = "1";

    this.dust = document.createElement("canvas");
    this.dust.className = "onirya-start-dust";
    this.dust.setAttribute("aria-hidden", "true");
    const ctx = this.dust.getContext("2d");
    if (!ctx) throw new Error("StartOverlay: canvas 2d indisponível");
    this.dustCtx = ctx;

    this.word = document.createElement("div");
    this.word.className = "onirya-wake-title is-visible onirya-start-title";
    this.word.setAttribute("aria-label", word);

    for (const ch of word) {
      const el = document.createElement("span");
      el.textContent = ch;
      this.word.appendChild(el);
      this.letters.push({ el, x: 0, y: 0, vx: 0, vy: 0 });
    }

    this.button = document.createElement("button");
    this.button.className = "onirya-wake-play is-visible";
    this.button.type = "button";
    this.button.textContent = startLabel;
    this.button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      onStart();
    });

    this.chrome = document.createElement("div");
    this.chrome.className = "onirya-chrome is-visible";

    const info = document.createElement("button");
    info.type = "button";
    info.className = "onirya-chrome-btn";
    info.setAttribute("aria-label", "About this place");
    info.textContent = "i";
    info.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.about.show();
    });

    const coffee = document.createElement("a");
    coffee.className = "onirya-chrome-btn";
    coffee.href = BUY_ME_A_COFFEE_URL;
    coffee.target = "_blank";
    coffee.rel = "noopener noreferrer";
    coffee.setAttribute("aria-label", "Buy me a coffee");
    coffee.innerHTML = coffeeSvg();

    this.chrome.append(info, coffee);

    this.root.append(this.white, this.dust, this.word, this.button, this.chrome);
    host.appendChild(this.root);
    this.about = new AboutOverlay(host);
    if (onOpenPanel) {
      const levels = document.createElement("button");
      levels.type = "button";
      levels.className = "onirya-wake-levels is-visible";
      levels.textContent = "levels";
      levels.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenPanel();
      });
      this.root.appendChild(levels);
      this.levelsBtn = levels;
    }
  }

  get visible(): boolean {
    return this.open;
  }

  dismiss(): void {
    if (!this.open) return;
    this.open = false;
    this.root.classList.add("is-leaving");
    this.white.style.opacity = "0";
    this.dust.style.opacity = "0";
    this.word.classList.remove("is-visible");
    this.button.classList.remove("is-visible");
    this.levelsBtn?.classList.remove("is-visible");
    this.chrome.classList.remove("is-visible");
    this.about.hide();
    this.leaveTimer = window.setTimeout(() => {
      this.root.classList.remove("is-active");
      this.root.setAttribute("aria-hidden", "true");
    }, 900);
  }

  reveal(): void {
    window.clearTimeout(this.leaveTimer);
    this.open = true;
    for (const letter of this.letters) {
      letter.x = 0;
      letter.y = 0;
      letter.vx = 0;
      letter.vy = 0;
      letter.el.style.transform = "";
    }
    this.root.classList.remove("is-leaving");
    this.root.classList.add("is-active");
    this.root.setAttribute("aria-hidden", "false");
    this.white.style.opacity = "1";
    this.dust.style.opacity = "1";
    this.word.classList.add("is-visible");
    this.button.classList.add("is-visible");
    this.levelsBtn?.classList.add("is-visible");
    this.chrome.classList.add("is-visible");
  }

  hideImmediate(): void {
    window.clearTimeout(this.leaveTimer);
    this.open = false;
    this.root.classList.remove("is-active", "is-leaving");
    this.root.setAttribute("aria-hidden", "true");
    this.white.style.opacity = "0";
    this.word.classList.remove("is-visible");
    this.button.classList.remove("is-visible");
    this.levelsBtn?.classList.remove("is-visible");
    this.chrome.classList.remove("is-visible");
    this.about.hide();
  }

  remove(): void {
    window.clearTimeout(this.leaveTimer);
    this.about.remove();
    this.root.remove();
  }

  update(
    dt: number,
    time: number,
    field: ForceField,
    divider: Divider,
    particles: WindParticles,
  ): void {
    if (this.root.classList.contains("is-active")) {
      this.syncDustSize();
      particles.paintMono(this.dustCtx);
    }
    if (!this.open) return;
    const hostRect = this.host.getBoundingClientRect();
    const cap = MAX_OFF;

    for (const letter of this.letters) {
      const box = letter.el.getBoundingClientRect();
      const wx = box.left - hostRect.left + box.width * 0.5 - letter.x;
      const wy = box.top - hostRect.top + box.height * 0.5 - letter.y;
      const wind = field.sample(wx, wy, time, divider);
      const ax = clamp(wind.x * WIND_PX, -MAX_ACCEL, MAX_ACCEL) - letter.x * SPRING - letter.vx * DAMP;
      const ay = clamp(wind.y * WIND_PX, -MAX_ACCEL, MAX_ACCEL) - letter.y * SPRING - letter.vy * DAMP;
      letter.vx += ax * dt;
      letter.vy += ay * dt;
      letter.x = clamp(letter.x + letter.vx * dt, -cap, cap);
      letter.y = clamp(letter.y + letter.vy * dt, -cap, cap);
      letter.el.style.transform = `translate(${letter.x.toFixed(2)}px, ${letter.y.toFixed(2)}px)`;
    }
  }

  private syncDustSize(): void {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(this.host.clientWidth));
    const h = Math.max(1, Math.round(this.host.clientHeight));
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);
    if (this.dust.width !== bw || this.dust.height !== bh) {
      this.dust.width = bw;
      this.dust.height = bh;
    }
    this.dustCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
}
